import axios from 'axios';

// Use relative URL - will be resolved against the current page's origin
const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors — skip for login/setup endpoints so their
// error messages can be displayed to the user instead of redirecting.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/setup');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (username: string, password: string) =>
    api.post('/auth/login', { username, password }),
  setup: (username: string, password: string) =>
    api.post('/auth/setup', { username, password }),
  status: () => api.get('/auth/status'),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  setInitialPassword: (newPassword: string) =>
    api.post('/auth/set-initial-password', { new_password: newPassword }),
};

// Users (admin only)
export const usersAPI = {
  list: () => api.get('/users'),
  get: (userId: number) => api.get(`/users/${userId}`),
  create: (data: { username: string; password: string; monthly_cost_limit?: number; is_admin?: boolean }) =>
    api.post('/users', data),
  invite: (data: { email: string; monthly_cost_limit?: number; is_admin?: boolean }) =>
    api.post('/users/invite', data),
  update: (userId: number, data: { monthly_cost_limit?: number; is_active?: boolean; password?: string }) =>
    api.put(`/users/${userId}`, data),
  delete: (userId: number) => api.delete(`/users/${userId}`),
  getMyCostStatus: () => api.get('/users/me/cost-status'),
};

// Chats
export const chatsAPI = {
  list: (projectId?: number) =>
    api.get('/chats', { params: { project_id: projectId } }),
  get: (chatId: number) => api.get(`/chats/${chatId}`),
  create: (data: { title?: string; query_type_id: number; project_id?: number }) =>
    api.post('/chats', data),
  createWithQuery: (data: { query_type_id: number; query: string; mode: string; project_id?: number; attachment_ids?: number[] }) =>
    api.post('/chats/with-query', data),
  createWithQueryAtomic: (data: { query_type_id: number; query: string; mode: string; project_id?: number; attachment_ids?: number[] }) =>
    api.post('/chats/with-query-atomic', data),
  update: (chatId: number, data: { title: string }) =>
    api.patch(`/chats/${chatId}`, data),
  delete: (chatId: number) => api.delete(`/chats/${chatId}`),
  submitQuery: (chatId: number, query: string, mode: string, attachmentIds?: number[]) =>
    api.post(`/chats/${chatId}/query`, { query, mode, attachment_ids: attachmentIds }),
  getQueryStatus: (chatId: number, taskId: string) =>
    api.get(`/chats/${chatId}/query/${taskId}`),
  saveQueryResult: (chatId: number, taskId: string) =>
    api.post(`/chats/${chatId}/query/${taskId}/save`),
  getTurnResponses: (chatId: number, turnId: number) =>
    api.get(`/chats/${chatId}/turns/${turnId}/responses`),
};

// Projects
export const projectsAPI = {
  list: () => api.get('/projects'),
  get: (projectId: number) => api.get(`/projects/${projectId}`),
  create: (data: { name: string; description?: string; retention_days?: number }) =>
    api.post('/projects', data),
  update: (projectId: number, data: any) => api.put(`/projects/${projectId}`, data),
  delete: (projectId: number) => api.delete(`/projects/${projectId}`),
};

// Query Types
export const queryTypesAPI = {
  list: () => api.get('/query-types'),
  get: (queryTypeId: number) => api.get(`/query-types/${queryTypeId}`),
  create: (data: any) => api.post('/query-types', data),
  update: (queryTypeId: number, data: any) => api.put(`/query-types/${queryTypeId}`, data),
  delete: (queryTypeId: number) => api.delete(`/query-types/${queryTypeId}`),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data: any) => api.put('/settings', data),
  listAPIKeys: () => api.get('/settings/api-keys'),
  saveAPIKey: (provider: string, apiKey: string) =>
    api.post('/settings/api-keys', { provider, api_key: apiKey }),
  testAPIKey: (provider: string, apiKey: string) =>
    api.post('/settings/api-keys/test', { provider, api_key: apiKey }),
  listEngines: () => api.get('/settings/engines'),
  listJudgmentTypes: () => api.get('/settings/judgment-types'),
};

// Attachments
export const attachmentsAPI = {
  upload: (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/attachments/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (attachmentId: number) => api.delete(`/attachments/${attachmentId}`),
};

// Documents
export const documentsAPI = {
  list: () => api.get('/documents'),
  download: (documentId: number) =>
    api.get(`/documents/${documentId}`, { responseType: 'blob' }),
  delete: (documentId: number) => api.delete(`/documents/${documentId}`),
};

// Stats
export const statsAPI = {
  getCosts: () => api.get('/stats/costs'),
  getAllCosts: () => api.get('/stats/costs/all'),
  getDailyCosts: (days: number = 30) =>
    api.get('/stats/costs/daily', { params: { days } }),
  getCostsByEngine: () => api.get('/stats/costs/by-engine'),
  getCostsByModel: () => api.get('/stats/costs/by-model'),
  getUsage: () => api.get('/stats/usage'),
};

// Reports
export const reportsAPI = {
  send: (data: {
    category: string;
    subject: string;
    message: string;
    attachment_ids?: number[];
  }) => api.post('/reports/send', data),
  getConfig: () => api.get('/reports/config'),
};

export default api;
